import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';

import { NAV_ITEMS } from '../../constants';
import { useAuth } from '../../context/AuthProvider';
import { useShift } from '../../context/ShiftProvider';
import useAuthenticatedRequest from '../../utils/TokenRefreshUtils';
import { getUserDisplayName } from '../../utils/JwtUtils';
import Drawer from '../common/Drawer';
import ShiftBadge from './ShiftBadge';

import rctLogo from '../../images/RCTLogo.png';

/**
 * Каркас застосунку: шапка, бічне меню, нижня навігація.
 *
 * Було: Toolbar із трьома безіменними іконками, що рендерився поруч із
 * Routes, і PanelMenu у бічній панелі — на телефоні до будь-якого розділу
 * треба було два тапи.
 *
 * Стало: постійна нижня навігація (основні розділи в один тап, як у будь-якому
 * мобільному застосунку) плюс бічне меню для підрозділів та виходу.
 */
const Layout = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef(null);
    const authenticatedRequest = useAuthenticatedRequest();
    const { isShiftOpen } = useShift();

    const [menuVisible, setMenuVisible] = useState(false);
    const [divisions, setDivisions] = useState([]);

    const userName = useMemo(() => getUserDisplayName(auth.token) ?? 'Касир', [auth.token]);

    const activeDivision = useMemo(
        () => divisions.find((division) => division.is_active) ?? null,
        [divisions]
    );

    useEffect(() => {
        let cancelled = false;

        const fetchDivisions = async () => {
            const url = `${process.env.REACT_APP_BACKEND_URL}kasa_shifts/get_cash_app_user_available_divisions/`;
            const options = { method: 'GET', headers: { 'Content-Type': 'application/json' } };

            try {
                const response = await authenticatedRequest(url, options);

                if (!response || response.status !== 200) {
                    return;
                }

                const parsed = await response.json();

                if (cancelled || !Array.isArray(parsed)) {
                    return;
                }

                setDivisions(parsed);

                // Раніше тут падало з TypeError, якщо жоден підрозділ
                // не був активним (find повертав undefined).
                const active = parsed.find((division) => division.is_active === true);

                if (active) {
                    Cookies.set('cashAppDivision', active.division_id);
                }
            } catch (error) {
                console.error('Не вдалося отримати список підрозділів', error);
            }
        };

        fetchDivisions();

        return () => {
            cancelled = true;
        };
    }, [authenticatedRequest]);

    const switchDivision = useCallback(
        async (divisionId) => {
            const url = `${process.env.REACT_APP_BACKEND_URL}kasa_shifts/set_division_for_cash_app_user/`;
            const options = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ division_id: divisionId }),
            };

            const response = await authenticatedRequest(url, options);

            if (response?.status === 201) {
                navigate(0);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Не вдалося змінити підрозділ',
                    detail: `Код помилки: ${response?.status ?? 'невідомо'}`,
                    life: 3000,
                });
            }
        },
        [authenticatedRequest, navigate]
    );

    const go = (path) => {
        navigate(path);
        setMenuVisible(false);
    };

    const confirmLogout = () => {
        confirmDialog({
            message: 'Вийти із застосунку? Відкрита зміна залишиться відкритою.',
            header: 'Вихід',
            icon: 'pi pi-sign-out',
            acceptLabel: 'Вийти',
            rejectLabel: 'Скасувати',
            acceptClassName: 'p-button-danger',
            accept: () => auth.logOut(),
        });
    };

    const isActive = (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`);

    // Каталогу товарів тісно в стандартному контейнері: на широкому
    // моніторі це означає три плитки в ряд і півтора екрана порожнечі
    // з боків. Решта екранів лишається вузькою — там довгі рядки
    // тексту й таблиці читаються гірше, коли розтягнуті.
    const isWideScreen = location.pathname.startsWith('/newsale');

    const visibleNavItems = NAV_ITEMS.filter(
        (item) => !item.requiresOpenShift || isShiftOpen
    );

    return (
        <div className="rc-shell">
            <Toast ref={toast} position="top-center" />
            <ConfirmDialog />

            <header className="rc-header">
                <button
                    type="button"
                    className="rc-header__brand"
                    onClick={() => go('/shift')}
                    aria-label="На головний екран"
                >
                    <img className="rc-header__logo" src={rctLogo} alt="" />
                    <span className="rc-header__wordmark">
                        RCT<em>CASH</em>
                    </span>
                </button>

                <div className="rc-header__context">
                    {activeDivision && (
                        <span className="rc-header__division" title={activeDivision.division_name}>
                            {activeDivision.division_name}
                        </span>
                    )}
                </div>

                <span className="rc-header__spacer" />

                <div className="rc-header__actions">
                    <ShiftBadge open={isShiftOpen} />
                    <Button
                        icon="pi pi-bars"
                        text
                        rounded
                        aria-label="Меню"
                        onClick={() => setMenuVisible(true)}
                    />
                </div>
            </header>

            <main className={`rc-main ${isWideScreen ? 'rc-main--wide' : ''}`.trim()}>
                <Outlet />
            </main>

            <nav className="rc-bottom-nav" aria-label="Основна навігація">
                {visibleNavItems.map((item) => (
                    <button
                        key={item.path}
                        type="button"
                        className={`rc-bottom-nav__item ${
                            isActive(item.path) ? 'rc-bottom-nav__item--active' : ''
                        }`.trim()}
                        onClick={() => go(item.path)}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                    >
                        <i className={item.icon} />
                        <span>{item.label}</span>
                    </button>
                ))}
            </nav>

            <Drawer
                visible={menuVisible}
                onHide={() => setMenuVisible(false)}
                title={
                    <span className="rc-menu-user">
                        <i className="pi pi-user" />
                        <span>
                            <span className="rc-menu-user__name">{userName}</span>
                            {activeDivision && (
                                <span className="rc-menu-user__meta">
                                    {activeDivision.division_name}
                                </span>
                            )}
                        </span>
                    </span>
                }
                flush
                footer={
                    <Button
                        label="Вийти"
                        icon="pi pi-sign-out"
                        severity="danger"
                        outlined
                        className="w-full"
                        onClick={() => {
                            setMenuVisible(false);
                            confirmLogout();
                        }}
                    />
                }
            >
                <div className="rc-menu-panel__body">
                    <div className="rc-menu-section-title">Розділи</div>
                    {NAV_ITEMS.map((item) => {
                        const disabled = item.requiresOpenShift && !isShiftOpen;

                        return (
                            <button
                                key={item.path}
                                type="button"
                                className={`rc-menu-item ${
                                    isActive(item.path) ? 'rc-menu-item--active' : ''
                                }`.trim()}
                                disabled={disabled}
                                style={disabled ? { opacity: 0.45 } : undefined}
                                onClick={() => go(item.path)}
                            >
                                <i className={item.icon} />
                                <span className="rc-menu-item__label">{item.label}</span>
                                {disabled && (
                                    <span className="rc-summary-row__sub">Зміна закрита</span>
                                )}
                            </button>
                        );
                    })}

                    {divisions.length > 1 && (
                        <>
                            <div className="rc-menu-section-title">Підрозділ</div>
                            {divisions.map((division) => (
                                <button
                                    key={division.division_id}
                                    type="button"
                                    className={`rc-menu-item ${
                                        division.is_active ? 'rc-menu-item--active' : ''
                                    }`.trim()}
                                    onClick={() => {
                                        if (!division.is_active) {
                                            switchDivision(division.division_id);
                                        }
                                    }}
                                >
                                    <i className="pi pi-building" />
                                    <span className="rc-menu-item__label">
                                        {division.division_name}
                                    </span>
                                    {division.is_active && <i className="pi pi-check" />}
                                </button>
                            ))}
                        </>
                    )}
                </div>
            </Drawer>
        </div>
    );
};

export default Layout;
